// Parts, Suppliers and Offers tabs of the redesigned workspace, and the two
// add pickers (Lane 1344, UI redesign slice 1; Ryan rounds 2 to 4). One line per
// cell, filters like a spreadsheet, figures from the server only.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import WorkspaceTable from './WorkspaceTable';
import SupplierFileEntry from './SupplierFileEntry';
import { HistoryMark, SupplierHistoryPanel } from './SupplierHistory';
import { formatMoney, formatUnits, formatUnitPrice, newKey } from './sourcingApi';

function price(value, currency) {
  return value === null || value === undefined ? null : formatUnitPrice(value, currency || 'USD');
}

function pct(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value) * 100;
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function signed(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  const text = formatMoney(Math.abs(n));
  return n >= 0 ? <span className="srce-good">{text}</span> : <span className="srce-neg">({text})</span>;
}

function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function readCols(key, fallback) {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) || 'null');
    return v && typeof v === 'object' ? { ...fallback, ...v } : fallback;
  } catch (e) { return fallback; }
}

function saveCols(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* a convenience only */ }
}

// ---- Add pickers --------------------------------------------------------------------

function Picker({ title, hint, fetchPage, columns, rowKey, addLabel, onAdd, onClose, chatHint, note }) {
  const [picked, setPicked] = useState(() => new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selected = useMemo(() => new Set(picked.keys()), [picked]);
  const onSelect = (keys, on, rows) => {
    const next = new Map(picked);
    keys.forEach((k, i) => { if (on) next.set(k, rows[i] || rows.find((r) => rowKey(r) === k)); else next.delete(k); });
    setPicked(next);
  };
  return (
    <div className="srce-overlay" role="dialog" aria-label={title}>
      <div className="srce-modal">
        <div className="srce-card2-h"><span className="srce-card2-t">{title}</span>
          <button type="button" className="srce-chip-x srce-push" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
          </button></div>
        <div className="srce-modal-b">
          <div className="srce-mut">{hint}</div>
          {note && <div className="srce-mut">{note}</div>}
          {error && <div className="srce-error" role="alert">{error}</div>}
          <WorkspaceTable fetchPage={fetchPage} columns={columns} rowKey={rowKey} selectable
            selected={selected} onSelect={onSelect} emptyText="Nothing matches these filters." ariaLabel={title} />
        </div>
        <div className="srce-modal-f">
          <span className="srce-mut srce-ell">{chatHint}</span>
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn" onClick={onClose}>Cancel</button>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || picked.size === 0}
              onClick={async () => {
                setBusy(true);
                setError('');
                // onAdd reports each row it saved, so a failure part way leaves only the
                // rows not yet added ticked (Codex UI slice 1 round 1).
                const done = (key) => setPicked((prev) => { const next = new Map(prev); next.delete(key); return next; });
                try {
                  await onAdd([...picked.values()], done);
                  onClose();
                } catch (e) { setError(e.message); } finally { setBusy(false); }
              }}>{busy ? 'Adding…' : `${addLabel}${picked.size ? ` (${picked.size})` : ''}`}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- Parts ------------------------------------------------------------------------

export function PartsView({ api, eventId, summary, refreshKey, onChanged, onEditLines }) {
  const [info, setInfo] = useState(null);
  const [filters, setFilters] = useState({});
  const [adding, setAdding] = useState(false);
  const fetchPage = useCallback((p) => api.table(eventId, 'parts', p), [api, eventId]);
  const [truncated, setTruncated] = useState(false);
  const fetchCandidates = useCallback((p) => api.table(eventId, 'part-candidates', p)
    .then((d) => { setTruncated(Boolean(d.truncated)); return d; }), [api, eventId]);
  const final = ['awarded', 'cancelled'].includes(summary.status);
  const columns = [
    { key: 'part', label: 'Part', weight: 1.1, filter: 'part',
      render: (r) => <span className="srce-row srce-nowrap srce-ell">{r.part_number}
        {r.added && <span className="srce-pill srce-pill--info">Added</span>}
        {!r.included && <span className="srce-pill srce-pill--gray">Left out</span>}</span>,
      title: (r) => r.part_number },
    { key: 'description', label: 'Description', weight: 2.4, filter: 'description', render: (r) => r.description },
    { key: 'site', label: 'Site', weight: 1.2, filter: 'site', render: (r) => r.site },
    { key: 'supplier', label: 'Supplier', weight: 1.6, filter: 'supplier', render: (r) => r.supplier },
    { key: 'quantity', label: 'Quantity', weight: 0.8, num: true, render: (r) => formatUnits(r.quantity) },
    { key: 'price', label: 'Price', weight: 0.8, num: true, render: (r) => price(r.price) },
    { key: 'spend', label: 'Spend', weight: 0.9, num: true, render: (r) => (r.spend === null ? null : formatMoney(r.spend)) },
  ];
  const s = info && info.summary;
  // Lane 1438: the spend is every supplier's; an event built from one supplier's parts
  // also shows that supplier's part of it (the same query, shown only when it adds up).
  const origin = s && s.origin_supplier;
  return (
    <div className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-ell">{s ? `${s.parts.toLocaleString()} parts at ${s.sites} ${s.sites === 1 ? 'site' : 'sites'}, ${formatMoney(s.spend)} spend on these parts in the last 12 months, all suppliers${origin ? (origin.spend === null ? ` (${origin.name}'s share is not shown: the purchase data changed since these figures were taken)` : ` (${formatMoney(origin.spend)} with ${origin.name})`) : ''}${s.excluded_lines ? `, ${s.excluded_lines} lines left out` : ''}` : ''}</span>
        {Object.keys(filters).length > 0 && <button type="button" className="ll-clr" onClick={() => setFilters({})}>Clear filters</button>}
        <span className="srce-push srce-row">
          {!final && <button type="button" className="srce-btn" onClick={onEditLines}>Edit lines</button>}
          {!final && <button type="button" className="srce-btn srce-btn--primary" onClick={() => setAdding(true)}>Add parts</button>}
        </span>
      </div>
      {info && info.change_in_progress && <div className="srce-mut srce-card2-f">A change is in progress: this list shows it. Suppliers see it after you publish the change.</div>}
      <WorkspaceTable fetchPage={fetchPage} columns={columns} rowKey={(r) => r.line_uid} refreshKey={refreshKey}
        filters={filters} onFiltersChange={setFilters} onData={setInfo} ariaLabel="Parts" emptyText="No parts match these filters." />
      {adding && (
        <Picker title="Add parts" fetchPage={fetchCandidates} rowKey={(r) => `${r.part_number}\u0000${r.site}`}
          hint={summary.status === 'draft'
            ? 'Parts bought in the last 12 months that are not in this event. Filter any column, tick the rows, then add them.'
            : 'Parts you add go into a change of the event. Suppliers see them only after you publish the change.'}
          chatHint="Or ask Wingman to add parts by site, by category or by the supplier you buy them from."
          note={truncated ? 'Only the 20,000 part-site pairs with the most spend are listed. For others, ask Wingman in the chat.' : null}
          addLabel="Add parts"
          columns={[
            { key: 'part', label: 'Part', weight: 1.1, filter: 'part', render: (r) => r.part_number },
            { key: 'description', label: 'Description', weight: 2.6, filter: 'description', render: (r) => r.description },
            { key: 'site', label: 'Site', weight: 1.2, filter: 'site', render: (r) => r.site },
            { key: 'supplier', label: 'Supplier', weight: 1.6, filter: 'supplier', render: (r) => r.supplier },
            { key: 'quantity', label: 'Quantity', weight: 0.8, num: true,
              render: (r) => (r.quantity === null ? 'You set it' : formatUnits(r.quantity)),
              title: (r) => (r.quantity === null ? 'Bought in more than one unit, or none on balance: you enter the volume after adding' : undefined) },
            { key: 'spend', label: 'Spend', weight: 0.9, num: true, render: (r) => formatMoney(r.spend) },
          ]}
          onAdd={async (rows) => {
            let fresh = await api.event(eventId);
            // After publish, parts go into a change (suppliers see nothing until it is
            // published); start one when none is in progress.
            if (['open', 'closed'].includes(fresh.status) && !fresh.amendment) {
              await api.amendmentAction(eventId, 'start', {}, fresh.revision);
              fresh = await api.event(eventId);
            }
            await api.importLines(eventId, rows.map((r) => ({ material_number: r.part_number, site: r.site })), fresh.revision);
            await onChanged();
          }}
          onClose={() => setAdding(false)} />
      )}
    </div>
  );
}

// ---- Suppliers --------------------------------------------------------------------

const RESPONSE_PILL = { submitted: 'srce-pill--ok', declined: 'srce-pill--gray', selected: 'srce-pill--gray' };

// "Add suppliers" on a draft: the company's suppliers not in the event (Lane 1440: also
// opened from the RFP invitations table).
export function AddSuppliersPicker({ api, eventId, onChanged, onClose }) {
  const fetchCandidates = useCallback((p) => api.table(eventId, 'supplier-candidates', p), [api, eventId]);
  return (
    <Picker title="Add suppliers" fetchPage={fetchCandidates} rowKey={(r) => r.supplier_id}
      hint="Your suppliers that are not in this event. Approved for: how many of this event's parts each one is approved for. Suppliers are contacted only when you publish."
      chatHint='Or ask Wingman, for example: "add the suppliers approved for these parts".'
      addLabel="Add suppliers"
      columns={[
        { key: 'supplier', label: 'Supplier', weight: 3, filter: 'supplier', render: (r) => r.supplier_name },
        { key: 'approved_for', label: 'Approved for', weight: 1, num: true, render: (r) => r.approved_for.toLocaleString() },
      ]}
      onAdd={async (rows, done) => {
        // One supplier per call; each call needs the event's revision at that moment.
        // The screen reloads after each one, so a later failure never hides earlier adds.
        for (const r of rows) {
          // eslint-disable-next-line no-await-in-loop
          const fresh = await api.event(eventId);
          try {
            // eslint-disable-next-line no-await-in-loop
            await api.act(eventId, 'POST', '/suppliers', { supplier_id: r.supplier_id }, fresh.revision);
          } catch (e) {
            // eslint-disable-next-line no-await-in-loop
            await onChanged();
            throw new Error(`${r.supplier_name}: ${e.message}`);
          }
          done(r.supplier_id);
        }
        await onChanged();
      }}
      onClose={onClose} />
  );
}

export function SuppliersView({ api, eventId, summary, refreshKey, onChanged, onOpenInvitations, onOpenQualifications }) {
  const [info, setInfo] = useState(null);
  const [adding, setAdding] = useState(false);
  // Lane 1394: the supplier whose emailed bid file the buyer is entering.
  const [entering, setEntering] = useState(null);
  // Lane 1395 QA (Ryan): a supplier row expands in place to show what we know about them.
  const [expanded, setExpanded] = useState(null);
  const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));
  const closeTo = (id) => {
    setExpanded(null);
    const b = document.getElementById(`sup-toggle-${id}`);
    if (b) b.focus();
  };
  const [tick, setTick] = useState(0);
  const fetchPage = useCallback((p) => api.table(eventId, 'suppliers', p), [api, eventId]);
  const isDraft = summary.status === 'draft';
  const columns = [
    { key: 'supplier', label: 'Supplier', weight: 2.2, filter: 'supplier',
      // A disclosure button (Lane 1395 QA): shows what we know about the supplier in place.
      render: (r) => (isDraft ? r.supplier_name : (
        <button type="button" id={`sup-toggle-${r.supplier_id}`} className="srce-disclose"
          aria-expanded={expanded === r.supplier_id} aria-controls={`wt-exp-${r.supplier_id}`}
          onClick={(e) => { e.stopPropagation(); toggle(r.supplier_id); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && expanded === r.supplier_id) { e.stopPropagation(); closeTo(r.supplier_id); } }}>
          <span className="srce-disclose-i" aria-hidden="true">{expanded === r.supplier_id ? '▾' : '▸'}</span>{r.supplier_name}
        </button>
      )), title: (r) => r.supplier_name },
    { key: 'response', label: 'Response', weight: 1.1, filter: 'response',
      render: (r) => <span className={`srce-pill ${RESPONSE_PILL[r.response] || 'srce-pill--info'}`}>{r.response_text}</span>,
      title: (r) => (r.decline_reason ? `Declined: ${r.decline_reason}` : r.response_text) },
    { key: 'priced', label: 'Priced', weight: 0.8, num: true, render: (r) => (r.priced === null ? null : r.priced.toLocaleString()) },
    { key: 'no_bid', label: 'No bids', weight: 0.8, num: true, render: (r) => (r.no_bid === null ? null : r.no_bid.toLocaleString()) },
    { key: 'approved_for', label: 'Approved for', weight: 0.9, num: true, render: (r) => r.approved_for.toLocaleString() },
    { key: 'incumbent_on', label: 'Incumbent on', weight: 0.9, num: true, render: (r) => r.incumbent_on.toLocaleString() },
    { key: 'last', label: 'Last submitted', weight: 1, num: true, render: (r) => shortDate(r.last_submitted_at) },
  ];
  if (!isDraft) {
    columns.push({ key: 'received', label: 'Received by', weight: 1.4, render: (r) => r.received_by || '-' });
    columns.push({ key: 'file', label: 'Bid file', weight: 0.9,
      render: (r) => (!['selected', 'removed'].includes(r.response) ? (
        <button type="button" className="ll-clr" onClick={(e) => { e.stopPropagation(); setEntering(r); }}>Enter a file</button>
      ) : null) });
  }
  return (
    <div className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-mut srce-ell">{info ? `Counts are parts, out of ${info.parts_total.toLocaleString()}.` : ''}</span>
        <span className="srce-push srce-row">
          <button type="button" className="srce-btn" onClick={onOpenQualifications}>Qualifications</button>
          {!isDraft && <button type="button" className="srce-btn" onClick={onOpenInvitations}>Invitations and contacts</button>}
          {isDraft && <button type="button" className="srce-btn srce-btn--primary" onClick={() => setAdding(true)}>Add suppliers</button>}
          {summary.status === 'open' && <button type="button" className="srce-btn srce-btn--primary" onClick={onOpenInvitations}>Invite suppliers</button>}
        </span>
      </div>
      {entering && (
        // Keyed by supplier: switching supplier starts a fresh panel (Codex r1 #6).
        <SupplierFileEntry key={`${eventId}-${entering.supplier_id}`} api={api} eventId={eventId} supplier={entering}
          canEnter={summary.status === 'open'}
          onClose={() => setEntering(null)} onEntered={() => { setTick((n) => n + 1); if (onChanged) onChanged(); }} />
      )}
      <WorkspaceTable fetchPage={fetchPage} columns={columns} rowKey={(r) => r.supplier_id} refreshKey={`${refreshKey}-${tick}`}
        onData={setInfo} ariaLabel="Suppliers" emptyText="No suppliers yet."
        onRowClick={isDraft ? undefined : (r) => toggle(r.supplier_id)}
        expandedKey={expanded}
        renderExpanded={isDraft ? undefined : (r) => (
          <div role="region" aria-label={`What we know about ${r.supplier_name}`} tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); closeTo(r.supplier_id); } }}>
            <SupplierHistoryPanel supplierId={r.supplier_id} />
          </div>
        )} />
      {adding && <AddSuppliersPicker api={api} eventId={eventId} onChanged={onChanged} onClose={() => setAdding(false)} />}
    </div>
  );
}

// ---- Offers -----------------------------------------------------------------------

const OFFER_COLS_KEY = 'srce-offer-columns';
const OFFER_COL_DEFAULTS = { description: true, quantity: true, current: true, best: true, bestVs: true,
  bestSupplier: true, spend: false, costBest: false, change: false, vsBest: false };
const OFFER_COL_LABELS = [['description', 'Description'], ['quantity', 'Quantity'], ['current', 'Current price'],
  ['best', 'Best bid'], ['bestVs', 'Best vs current'], ['bestSupplier', 'Best supplier'],
  ['spend', 'Spend (12 months)'], ['costBest', 'Cost at best bid'], ['change', 'Change'], ['vsBest', '% above best, per supplier']];

const CELL_TEXT = { no_bid: 'No bid', withdrawn: 'Withdrawn', expired: 'Expired',
  needs_acknowledgement: 'To confirm', needs_resubmission: 'To requote', not_in_scope: '-' };

// At most this many supplier columns by default (the most parts priced first); the
// Columns menu shows or hides each one, so a wide event never crushes the table.
const DEFAULT_SUPPLIER_COLUMNS = 6;

// Offers 80% or more below today's price (Lane 1393): most likely a typo or a different
// unit, so they are named above the table, never only behind a hover mark.
export function SuspectPrices({ rows, currency }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="srce-suspect" role="status">
      <div className="srce-warn">{rows.length === 1 ? 'Check this offer' : `Check these ${rows.length} offers`}: 80% or more below today's price. It may be a typo or a different unit.</div>
      <div className="srce-mini srce-mini--5">
        <div className="srce-mini-h"><span>Supplier</span><span>Part</span><span className="srce-wt-num">Offer</span>
          <span className="srce-wt-num">Today</span><span className="srce-wt-num">Below today</span></div>
        {rows.map((r) => (
          <div key={`${r.supplier_id}-${r.part_number}`} className="srce-mini-r">
            <span className="srce-ell" title={r.supplier_name}>{r.supplier_name}</span>
            <span className="srce-ell" title={r.part_number}>{r.part_number}</span>
            <span className="srce-wt-num">{price(r.price, currency)}</span>
            <span className="srce-wt-num">{price(r.today_price, currency)}</span>
            <span className="srce-wt-num srce-neg">{r.below_percent}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// A bid column's header: the first real word of the supplier's name. Initials ("A W
// Chesterton", "A.W. Chesterton") are passed over; the full name is on hover (Lane 1395 QA).
export function shortSupplierName(name) {
  const words = String(name || '').split(/[\s,]+/).filter(Boolean);
  const real = words.find((w) => w.length > 1 && !/^([A-Za-z]\.)+$/.test(w));
  return real || words[0] || String(name || '');
}

// A price the buyer types: above 0, at most 4 decimals (the server's own rule).
const EDIT_PRICE_RE = /^\d{1,10}(\.\d{1,4})?$/;
const editKey = (sid, pn) => `${sid}|${pn}`;
// A typed price differs from the current one ("1.5" and "1.50" are the same price).
const isChange = (value, from) => {
  const v = value.trim();
  if (v === from) return false;
  return !(from !== '' && EDIT_PRICE_RE.test(v) && Number(v) === Number(from));
};

function editedTitle(c) {
  const e = c.edited;
  if (!e) return null;
  const day = e.at ? new Date(e.at).toLocaleDateString() : '';
  return `Changed by ${e.by}${day ? `, ${day}` : ''}${e.reason ? `: ${e.reason}` : ''}`;
}

// A compare cell (Lane 1395 QA): "then -> now" on one line. The change is coloured only
// when both prices answered the same version of the event (Codex QA r2 plan #3).
// A quote made when the part's currency or unit was different (Codex QA r2 code #1).
function basisText(side) {
  const b = side.basis || {};
  return `${side.price} ${b.currency || ''}${b.unit ? ` per ${b.unit}` : ''}`.trim();
}
function sideText(side, currency) {
  if (!side) return '-';
  if (side.state === 'no_bid') return 'No bid';
  if (side.state === 'other_basis') return basisText(side);
  if (side.state !== 'offer' || side.price === null || side.price === undefined) return CELL_TEXT[side.state] || '-';
  return price(side.price, currency);
}
function compareCell(c, currency) {
  const now = ['offer', 'no_bid', 'other_basis'].includes(c.state)
    ? { state: c.state, price: c.price, basis: c.basis } : null;
  const then = c.before;
  if (!now && !then) return null;
  const same = now && then && now.state === 'offer' && then.state === 'offer'
    && then.answers_version === c.answers_version && now.price !== null && then.price !== null;
  const d = same ? Number(now.price) - Number(then.price) : 0;
  const pencil = (side) => (side && side.edited ? <span className="srce-edited" aria-label={editedTitle(side)}>✎</span> : null);
  return (
    <span className={same && d < 0 ? 'srce-good' : same && d > 0 ? 'srce-neg' : ''}>
      {sideText(then, currency)}{pencil(then)} {'\u2192'} {sideText(now, currency)}{pencil(c)}
    </span>
  );
}
function compareTitle(c, compare, show, currency) {
  const nowLabel = show === null ? 'Latest' : `Round ${show}`;
  const then = c.before ? sideText(c.before, currency) : `not quoted in round ${compare}`;
  const now = ['offer', 'no_bid', 'other_basis'].includes(c.state) ? sideText(c, currency) : (CELL_TEXT[c.state] || 'nothing');
  const changed = (c.before && c.answers_version !== null && c.before.answers_version !== c.answers_version)
    || c.state === 'other_basis' || (c.before && c.before.state === 'other_basis')
    ? ' The part changed between these rounds, so the prices may not compare.' : '';
  const edits = [c.before && c.before.edited ? `Round ${compare} price: ${editedTitle(c.before)}.` : '',
    c.edited ? `${nowLabel} price: ${editedTitle(c)}.` : ''].filter(Boolean).join(' ');
  return `Round ${compare}: ${then}. ${nowLabel}: ${now}.${changed}${edits ? ` ${edits}` : ''}`;
}

export function OffersView({ api, eventId, refreshKey, onOpenBidTools, canEdit }) {
  const [info, setInfo] = useState(null);
  const [cols, setCols] = useState(() => readCols(OFFER_COLS_KEY, OFFER_COL_DEFAULTS));
  const supKey = `srce-offer-suppliers-${eventId}`;
  const [shownSup, setShownSup] = useState(() => readCols(supKey, null));
  const [menu, setMenu] = useState(false);
  // Edit offers (Lane 1395 QA, Ryan): the offer revision is pinned when editing starts;
  // a page read at another revision ends the session (Codex plan #4).
  const [editing, setEditing] = useState(null);
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editNotice, setEditNotice] = useState('');
  const [tick, setTick] = useState(0);
  const saveKey = useRef(newKey());
  const editingRef = useRef(null);
  editingRef.current = editing;
  // The session also pins its event (the view is keyed by event too).
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  // What the table shows (Lane 1395 QA, Ryan: "see what it was in the past"): the latest
  // offers, or what was quoted in one round; optionally compared with another round.
  const [show, setShow] = useState(null);       // null: latest; a number: that round
  const [compare, setCompare] = useState(null); // null: no comparison; a round number
  const [supMenu, setSupMenu] = useState(false);
  const [filters, setFilters] = useState({});
  const fetchPage = useCallback((p) => api.table(eventId, 'offers', {
    ...p, round: show === null ? undefined : show, compare: compare === null ? undefined : compare,
  }), [api, eventId, show, compare]);
  const onData = useCallback((d) => {
    setInfo(d);
    const e = editingRef.current;
    if (e && d && (d.offer_revision !== e.revision || e.eventId !== eventIdRef.current)) {
      setEditing(null);
      setEdits({});
      setEditError('Offers changed since you started editing, so nothing was saved. Edit again.');
    }
  }, []);
  const currency = info ? info.currency : 'USD';
  // Every supplier in the event can be shown; a declined one is off by default and can be
  // turned on (its price can still be changed after a call; Codex plan #7).
  const pickable = info ? info.suppliers : [];
  const bidding = pickable.filter((s) => s.status !== 'declined');
  const defaultShown = Object.fromEntries([...bidding].sort((a, b) => b.priced - a.priced)
    .map((s, i) => [s.supplier_id, i < DEFAULT_SUPPLIER_COLUMNS]));
  const supOn = (id) => (shownSup && id in shownSup ? shownSup[id] : defaultShown[id]);
  const suppliers = pickable.filter((s) => supOn(s.supplier_id));
  const declined = info ? info.suppliers.filter((s) => s.status === 'declined') : [];
  const touch = (next) => { setEdits(next); saveKey.current = newKey(); };
  // The current price as the box shows it: at least 2 decimals, never trailing zeros past
  // them ("1.5000" -> "1.50", "3.9999" stays).
  const original = (c) => {
    if (c.state !== 'offer' || c.price === null || c.price === undefined) return '';
    const [whole, dec = ''] = String(c.price).split('.');
    return `${whole}.${dec.replace(/0+$/, '').padEnd(2, '0')}`;
  };
  const changed = Object.entries(edits).filter(([, v]) => isChange(v.value, v.from));
  const invalid = changed.filter(([, v]) => !EDIT_PRICE_RE.test(v.value.trim()) || Number(v.value) <= 0);
  const startEdit = () => {
    setEditError('');
    setEditNotice('');
    setEdits({});
    setReason('');
    saveKey.current = newKey();
    setEditing({ revision: info.offer_revision, eventId });
  };
  const cancelEdit = () => { setEditing(null); setEdits({}); setEditError(''); };
  const saveEdits = async () => {
    if (editing.eventId !== eventId) { cancelEdit(); return; }
    setSaving(true);
    setEditError('');
    try {
      const r = await api.editOffers(editing.eventId, {
        expected_offer_revision: editing.revision, reason: reason.trim(),
        changes: changed.map(([k, v]) => {
          const [sid, ...pn] = k.split('|');
          return { supplier_id: Number(sid), part_number: pn.join('|'), price: v.value.trim() };
        }) }, saveKey.current);
      const n = (r.result && r.result.changed) || changed.length;
      setEditing(null);
      setEdits({});
      setEditNotice(`Saved ${n === 1 ? '1 price' : `${n.toLocaleString()} prices`}. Scenarios recalculate when you open them.`);
      setTick((t) => t + 1);
    } catch (e) {
      if (e.code === 'offers_changed') { setEditing(null); setEdits({}); setTick((t) => t + 1); }
      setEditError(e.message);
    } finally { setSaving(false); }
  };
  const columns = [{ key: 'part', label: 'Part', weight: 1.1, filter: 'part', render: (r) => r.part_number }];
  if (cols.description) columns.push({ key: 'description', label: 'Description', weight: 1.8, filter: 'description', render: (r) => r.description });
  if (cols.quantity) columns.push({ key: 'quantity', label: 'Quantity', weight: 0.8, num: true, render: (r) => formatUnits(r.volume) });
  if (cols.current) columns.push({ key: 'current', label: 'Current price', weight: 0.9, num: true,
    render: (r) => (r.best.current_price_varies ? 'Varies' : price(r.best.current_price, currency)),
    title: (r) => (r.best.current_price_varies ? "Today's price differs between its sites"
      : "Today's price: the last 12 months' average price paid") });
  if (cols.best) columns.push({ key: 'best', label: 'Best bid', weight: 0.9, num: true, render: (r) => price(r.best.best_price, currency),
    title: (r) => (r.best.sites_not_calculated
      ? `${r.best.sites_not_calculated} of this part's site lines cannot be calculated yet (a missing volume, a volume not in whole buying steps, or mixed units)` : undefined) });
  if (cols.bestVs) columns.push({ key: 'bestVs', label: 'Best vs current', weight: 0.9, num: true,
    render: (r) => pct(r.best.best_vs_current),
    cls: (r) => (r.best.best_vs_current === null ? '' : Number(r.best.best_vs_current) < 0 ? 'srce-good' : 'srce-neg'),
    title: (r) => (r.best.best_vs_current === null ? 'No price today to compare with'
      : r.best.sites_compared < r.best.sites_total ? `On ${r.best.sites_compared} of ${r.best.sites_total} sites` : undefined) });
  if (cols.bestSupplier) columns.push({ key: 'bestSupplier', label: 'Best supplier', weight: 1.2, filter: 'best_supplier',
    render: (r) => (r.best_supplier ? `${r.best_supplier}${r.best_needs_qualification ? ' *' : ''}` : null),
    title: (r) => (r.best_needs_qualification ? `${r.best_supplier}: needs qualification for this part` : r.best_supplier) });
  if (cols.spend) columns.push({ key: 'spend', label: 'Spend (12 months)', weight: 1, num: true, render: (r) => formatMoney(r.best.spend) });
  if (cols.costBest) columns.push({ key: 'costBest', label: 'Cost at best bid', weight: 1, num: true,
    render: (r) => (r.best.cost_at_best === null ? null : formatMoney(r.best.cost_at_best)),
    title: () => 'The best bid on the part\'s whole quantity, every site' });
  if (cols.change) columns.push({ key: 'change', label: 'Change', weight: 0.9, num: true,
    render: (r) => signed(r.best.change),
    title: (r) => `Today's cost (the last 12 months' average price paid, at this event's quantity) minus the cost at the best bid${
      r.best.sites_compared < r.best.sites_total ? `, on ${r.best.sites_compared} of ${r.best.sites_total} sites` : ''}` });
  suppliers.forEach((s) => {
    const cellOf = (r) => r.cells.find((c) => c.supplier_id === s.supplier_id) || { state: 'none' };
    // A short column name (the first real word, as in the agreed mockup); the full name on hover.
    columns.push({ key: `s${s.supplier_id}`, label: `${shortSupplierName(s.supplier_name)}${s.status === 'declined' ? ' (declined)' : ''}`,
      // A compare cell holds two prices ("then -> now"), so it gets twice the room.
      fullLabel: s.supplier_name, weight: compare !== null ? 1.8 : 0.9, num: true, mark: <HistoryMark supplierId={s.supplier_id} />,
      render: (r) => {
        const c = cellOf(r);
        if (editing) {
          const k = editKey(s.supplier_id, r.part_number);
          const from = original(c);
          const value = edits[k] ? edits[k].value : from;
          const on = isChange(value, from);
          const bad = edits[k] && on && (!EDIT_PRICE_RE.test(value.trim()) || Number(value) <= 0);
          return (
            <input className={`srce-edit-in${on ? ' srce-edit-in--on' : ''}${bad ? ' srce-edit-in--bad' : ''}`}
              inputMode="decimal" value={value} placeholder={CELL_TEXT[c.state] || '-'} disabled={saving}
              aria-label={`Price from ${s.supplier_name} for ${r.part_number}`} aria-invalid={Boolean(bad)}
              onChange={(e) => touch({ ...edits, [k]: { value: e.target.value, from } })} />
          );
        }
        if (compare !== null) return compareCell(c, currency);
        if (c.state === 'other_basis') {
          return <span className="srce-mut" title="Quoted when the part's currency or unit was different; not compared">{basisText(c)}</span>;
        }
        if (c.state !== 'offer') return CELL_TEXT[c.state] || null;
        const setAside = (c.flags || []).includes('min_order_above_volume');
        const isBest = r.best.best_supplier_id === s.supplier_id;
        const warn = (c.flags || []).filter((f) => f !== 'min_order_above_volume').length > 0;
        return (
          <span className={setAside ? 'srce-mut' : isBest ? 'srce-low' : ''}>{price(c.price, currency)}{warn ? ' !' : ''}
            {c.edited && <span className="srce-edited" aria-label={editedTitle(c)}>✎</span>}</span>
        );
      },
      title: (r) => {
        const c = cellOf(r);
        if (compare !== null) return compareTitle(c, compare, show, currency);
        if (c.state !== 'offer') return undefined;
        const parts = [];
        if (c.edited) parts.push(editedTitle(c));
        if ((c.flags || []).includes('min_order_above_volume')) parts.push(`Minimum order ${formatUnits(c.min_order_units)} is above the quantity: set aside`);
        const other = (c.flags || []).filter((f) => f !== 'min_order_above_volume');
        if (other.length) parts.push(`Check: ${other.join(', ').replace(/_/g, ' ')}`);
        if (c.lead_time_days) parts.push(`Lead time ${c.lead_time_days} days`);
        return parts.join('. ') || undefined;
      } });
    if (cols.vsBest && compare === null) columns.push({ key: `v${s.supplier_id}`, label: 'vs best', weight: 0.6, num: true,
      render: (r) => { const c = cellOf(r); return c.above_best === null || c.above_best === undefined ? null : pct(c.above_best); } });
  });
  const setCol = (k) => { const next = { ...cols, [k]: !cols[k] }; setCols(next); saveCols(OFFER_COLS_KEY, next); };
  const setSup = (id) => {
    const next = { ...defaultShown, ...(shownSup || {}), [id]: !supOn(id) };
    setShownSup(next);
    saveCols(supKey, next);
  };
  const rounds = info && info.round ? Array.from({ length: info.round }, (_, i) => i + 1) : [];
  // The table shows the chosen view only once its own answer is in (Codex QA r2 code #2):
  // until then the old rows are dimmed and nothing can be edited.
  const loaded = info && info.view ? info.view : null;
  const ready = Boolean(loaded && (loaded.round ?? null) === show && (loaded.compare ?? null) === compare);
  const latest = show === null && compare === null && ready && info.offer_revision !== undefined;
  // Earlier -> later only: the compared round is before the shown one (Codex QA r2 code #4).
  const compareChoices = rounds.filter((n) => n < (show === null ? (info ? info.round + 1 : 0) : show));
  const pickShow = (v) => {
    cancelEdit();
    const next = v === '' ? null : Number(v);
    setShow(next);
    if (compare !== null && next !== null && compare >= next) setCompare(null);
  };
  const pickCompare = (v) => { cancelEdit(); setCompare(v === '' ? null : Number(v)); };
  const shownCount = pickable.filter((x) => supOn(x.supplier_id)).length;
  const setAllSup = (on) => {
    const next = Object.fromEntries(pickable.map((x) => [x.supplier_id, on]));
    setShownSup(next);
    saveCols(supKey, next);
  };
  const anyFilter = Object.keys(filters).length > 0;
  const title = !ready ? 'Loading…' : show === null ? `Latest offers${info ? `, round ${info.round}` : ''}` : `Quoted in round ${show}`;
  return (
    <div className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-card2-t">{title}{ready && compare !== null ? `, compared with round ${compare}` : ''}</span>
        <span className="srce-push srce-row">
          {canEdit && !editing && info && latest && (
            <button type="button" className="srce-btn" onClick={startEdit}>Edit offers</button>
          )}
          <button type="button" className="srce-btn" onClick={onOpenBidTools}>Supplier responses and notes</button>
          <span className="srce-dd">
            <button type="button" className="srce-btn" aria-expanded={menu} onClick={() => setMenu(!menu)}>Columns</button>
            {menu && (
              <div className="srce-ddpanel srce-ddpanel--right" role="menu">
                {OFFER_COL_LABELS.map(([k, label]) => (
                  <label key={k} className="srce-fitem"><input type="checkbox" checked={Boolean(cols[k])} onChange={() => setCol(k)} />{label}</label>
                ))}
                <div className="srce-row"><button type="button" className="srce-btn srce-btn--sm srce-push" onClick={() => setMenu(false)}>Done</button></div>
              </div>
            )}
          </span>
        </span>
      </div>
      <div className="srce-bidbar" role="group" aria-label="What the table shows">
        {rounds.length > 0 && (
          <label className="srce-row">Show
            <select className="srce-input" value={show === null ? '' : String(show)} disabled={Boolean(editing)}
              onChange={(e) => pickShow(e.target.value)}>
              <option value="">Latest offers</option>
              {rounds.map((n) => <option key={n} value={String(n)}>Quoted in round {n}</option>)}
            </select>
          </label>
        )}
        {compareChoices.length > 0 && (
          <label className="srce-row">Compare with
            <select className="srce-input" value={compare === null ? '' : String(compare)} disabled={Boolean(editing)}
              onChange={(e) => pickCompare(e.target.value)}>
              <option value="">No comparison</option>
              {compareChoices.map((n) => <option key={n} value={String(n)}>Round {n}</option>)}
            </select>
          </label>
        )}
        <span className="srce-dd">
          <button type="button" className="srce-btn" aria-expanded={supMenu} onClick={() => setSupMenu(!supMenu)}>
            Suppliers ({shownCount} of {pickable.length})</button>
          {supMenu && (
            <div className="srce-ddpanel" role="menu">
              <div className="srce-row">
                <button type="button" className="srce-btn srce-btn--sm" onClick={() => setAllSup(true)}>All</button>
                <button type="button" className="srce-btn srce-btn--sm" onClick={() => setAllSup(false)}>None</button>
              </div>
              {pickable.map((s) => (
                <label key={s.supplier_id} className="srce-fitem" title={s.supplier_name}>
                  <input type="checkbox" checked={Boolean(supOn(s.supplier_id))} onChange={() => setSup(s.supplier_id)} />
                  <span className="srce-ell">{s.supplier_name}{s.status === 'declined' ? ' (declined)' : ''}</span></label>
              ))}
              <div className="srce-row"><button type="button" className="srce-btn srce-btn--sm srce-push" onClick={() => setSupMenu(false)}>Done</button></div>
            </div>
          )}
        </span>
        <span className="srce-mut">Parts: use the filter on the Part column.</span>
        {anyFilter && <button type="button" className="ll-clr" onClick={() => setFilters({})}>Clear filters</button>}
      </div>
      <SuspectPrices rows={info ? info.suspect_prices : null} currency={currency} />
      <div className="srce-mut srce-card2-f">Lowest bid in green. An offer whose minimum order is above the part's quantity is grey (it cannot be used). * the best supplier needs qualification for the part. ! a bid check to look at (hover for details). ✎ a price your team changed (hover for who and why).</div>
      {editError && <div className="srce-error srce-card2-f" role="alert">{editError}</div>}
      {editNotice && <div className="srce-good srce-card2-f" role="status">{editNotice}</div>}
      {editing && (
        <div className="srce-edit-bar" data-testid="offer-edit-bar">
          <span className="srce-strong">{changed.length === 1 ? '1 price changed' : `${changed.length.toLocaleString()} prices changed`}</span>
          {invalid.length > 0 && <span className="srce-neg">A price must be above 0 with at most 4 decimals.</span>}
          <input className="srce-input srce-edit-why" placeholder="Reason (for your team, for example: phone call with the supplier)"
            aria-label="Reason for the change" maxLength={500} value={reason}
            onChange={(e) => { setReason(e.target.value); saveKey.current = newKey(); }} />
          <button type="button" className="srce-btn" disabled={saving} onClick={cancelEdit}>Cancel</button>
          <button type="button" className="srce-btn srce-btn--primary"
            disabled={saving || changed.length === 0 || invalid.length > 0 || !reason.trim()} onClick={saveEdits}>
            {saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}
      <div className={ready ? '' : 'srce-stale'} aria-busy={!ready}>
        <WorkspaceTable fetchPage={fetchPage} columns={columns} rowKey={(r) => r.part_number} refreshKey={`${refreshKey}|${tick}`}
          onData={onData} ariaLabel="Bids" emptyText="No parts match these filters."
          filters={filters} onFiltersChange={setFilters} resetKey={`${show}|${compare}`} />
      </div>
      {declined.map((s) => (
        <div key={s.supplier_id} className="srce-mut srce-card2-f">{s.supplier_name} declined{s.decline_reason ? `: "${s.decline_reason}"` : '.'}</div>
      ))}
    </div>
  );
}
